import { useState } from "react";

import type { LibraryAsset } from "@/api/library";
import { useCapability } from "@/auth/SessionContext";
import { lifecycleRefusal } from "@/lib/archiveRefusal";
import { toneDocuments } from "@/lib/backgroundProfile";
import { findNamedRow, namedRows, updateNamedDocument } from "@/lib/namedParts";
import { PageHeader } from "@/shell/PageHeader";
import { Button } from "@/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/ui/Card";
import { Tooltip } from "@/ui/Tooltip";
import { AssetAuditTrail } from "@/ui/library/AssetAuditTrail";
import { SaveStatusLine } from "@/ui/library/AssetEditor";
import {
  LifecycleDialog,
  type LifecycleMode,
} from "@/ui/library/LifecycleDialog";
import { LoadingState, UnavailableState } from "@/ui/states";

import { BackgroundToneForm } from "./BackgroundToneForm";
import { NamedAssetMeta } from "./NamedAssetChrome";
import { NamedDocumentActions, NamedDocumentEditor } from "./NamedDocumentEditor";
import {
  NamedConflictRecovery,
  NamedProblems,
  NamedRefusal,
} from "./NamedDraftStates";
import { NamedReadFailureState } from "./NamedReadFailure";
import { NamedRowList } from "./NamedRowList";
import { NewBackgroundProfileDialog } from "./NewBackgroundProfileDialog";
import { useNamedDraft } from "./useNamedDraft";

/**
 * One shared BG interview profile, authored as named documents (#914).
 *
 * ## THIS IS THE SCREEN THE `Set files` GROUP LEADS TO
 *
 * The Lovable reference shows `/library/bg-sets` with a `SET FILES` group of
 * three named rows — `bg_conversation`, `bg_extraction`, `preamble_config` —
 * each opening a named page with a large prompt field, `Back`, `Details` and
 * `Save` (`/library/bg-sets/file/bg_conversation`). The S7 contract corrects
 * what that group MEANS: "Set files groups one profile's named parts, not
 * arbitrary files or an aggregate BG set". Those three files are not library
 * assets in this product and never were — the seed manifest imports the seven
 * DOMAIN files alone, deliberately, so that pinning one clinical set never
 * claims the common files.
 *
 * So the grouping is kept and its members are this family's: a profile is one
 * asset, and its named parts are `conversation_prompt`, `personality` and
 * `tone`. #859 lists the profiles in the catalog's `Set files` group; this page
 * is where one profile's named parts are listed and opened, and each row's
 * editor is the reference's file editor with the reference's controls.
 *
 * ## TWO EDITORS, CHOSEN BY WHAT THE DOCUMENT IS
 *
 * The two prompts take `NamedDocumentEditor` unchanged — they are exactly the
 * large named prompt field the reference shows. `tone` does NOT: it is a
 * STRUCTURED part, and that editor deliberately refuses to author one because
 * `StructuredPart.data` is an unconstrained `JsonValue`. #858 published this
 * one role's shape, so `BackgroundToneForm` renders the service's own contract
 * as named controls — the same warrant `BackgroundDomainPage` holds for
 * `domains`, and the reason neither surface is a JSON textarea.
 *
 * ## WHAT IS DELIBERATELY NOT HERE
 *
 *   * **A `personality_file` / `conversation_prompt_file` field.** The service
 *     refuses both as authored input and generates them at emission from the
 *     placement table (S7 §4). A control for either would be the storage-path
 *     editor the bounded scope forbids, and nothing typed into one could be
 *     published.
 *   * **A variant directory or path editor.** The profile's coordinate IS the
 *     variant, it is immutable once stored, and it is shown under `Details` as
 *     evidence rather than offered as a control.
 *   * **Rename, and a per-row description.** There is no rename route, and a
 *     row's name is its role — service vocabulary, not authored text. Both are
 *     the divergences `MainConcernAssetPage` records for the same reasons.
 */

/** The library rail section this family shares with BG domains (#858/#859). */
const BREADCRUMBS = [
  { label: "Clinical library", to: "/library" },
  { label: "BG sets", to: "/library/bg-sets" },
];

/** What the row list is, in the reference's own word for it. */
const ROWS_TITLE = "Set files";

const ROWS_DESCRIPTION =
  "The shared prompts and tone every background area of this profile is read under at runtime. All three are required — the clinical engine will not run an interview for a profile missing one.";

export function BackgroundProfilePage({ asset }: { asset: LibraryAsset }) {
  const state = useNamedDraft(asset);
  const write = useCapability("library.write");
  const restore = useCapability("library.restore");
  const [openRole, setOpenRole] = useState<string | null>(null);
  const [lifecycle, setLifecycle] = useState<LifecycleMode | null>(null);
  const [copying, setCopying] = useState(false);

  if (state.status === "loading") {
    return (
      <>
        <PageHeader breadcrumbs={BREADCRUMBS} title={asset.name} />
        <LoadingState label="Loading interview profile content" rows={4} />
      </>
    );
  }

  if (state.status === "error") {
    return (
      <>
        <PageHeader breadcrumbs={BREADCRUMBS} title={asset.name} />
        <NamedReadFailureState
          failure={state.failure}
          isPaused={state.isPaused}
          retry={state.retry}
          subject="Interview profile content"
          title="This interview profile's content could not be loaded"
          description={`The Editor Service did not return the content at ${asset.latestVersion}, so there is nothing to edit yet.`}
          deniedDescription="You are not authorized to read this asset's content."
          offlineDescription={`The content at ${asset.latestVersion} could not be reached, so there is nothing to edit yet. It loads on its own when the connection returns.`}
        />
      </>
    );
  }

  if (state.status === "unsupported") {
    return (
      <>
        <PageHeader breadcrumbs={BREADCRUMBS} title={asset.name} />
        <UnavailableState
          title="This asset's content is not a set of documents"
          description="A shared interview profile is authored as named documents, and this asset's stored content is a different shape. Its stored content is unchanged."
        />
      </>
    );
  }

  const { editor } = state;
  const rows = namedRows(editor.parts, "background_profile");
  const open = openRole === null ? null : findNamedRow(rows, openRole);

  const archiveMode: LifecycleMode = asset.capabilities.archived
    ? "unarchive"
    : "archive";
  // Direction-specific caller gate (#647): archiving is `library.write`,
  // restoring is the admin/operations `library.restore` the ratified contract
  // reserves — the same split the service enforces on the two routes.
  const archiveRefusal = lifecycleRefusal(
    asset,
    archiveMode === "unarchive" ? restore.reason : write.reason,
  );
  const archiveLabel = archiveMode === "archive" ? "Archive" : "Unarchive";

  const meta = (identityRow?: string) => (
    <NamedAssetMeta
      asset={asset}
      version={editor.pinned}
      identity={identityRow ?? `bg-sets/${asset.id}`}
      details={
        // THE VARIANT, READ-ONLY, as the service states it. A project selects a
        // profile by this coordinate — it becomes `flow_config.bg_variant` —
        // and a clinician reconciling a project's roster against the library
        // needs to see it. Under `Details` rather than on the line, because it
        // is technical evidence rather than navigation.
        <div className="break-all">
          interview_profile:{" "}
          {asset.placementCoordinate ??
            "owned by the base pack manifest, not by a coordinate"}
        </div>
      }
    />
  );

  const saveControls = editor.readOnly ? null : (
    <div className="flex flex-wrap items-center gap-3">
      <SaveStatusLine
        status={
          editor.saving
            ? "saving"
            : editor.failure !== null
              ? "error"
              : editor.dirty
                ? "dirty"
                : editor.lastSavedAt
                  ? "saved"
                  : "clean"
        }
        lastSavedAt={editor.lastSavedAt}
      />
      <Button
        variant="outline"
        disabled={!editor.dirty || editor.saving}
        onClick={editor.discard}
      >
        Discard
      </Button>
    </div>
  );

  // THE ROW EDITOR IS A SCREEN OF ITS OWN, reached by Open and returned from by
  // Back — the reference's own navigation, and `MainConcernAssetPage`'s. It
  // replaces the list rather than sitting beside it, and its breadcrumb carries
  // the profile back to the list.
  if (open) {
    const isTone = open.kind === "structured";
    return (
      <>
        <PageHeader
          breadcrumbs={[
            ...BREADCRUMBS,
            { label: asset.name, to: `/library/assets/${asset.id}` },
            { label: open.label },
          ]}
          title={open.label}
          description={
            isTone
              ? "How this profile opens the session and moves between background areas"
              : "Shared prompt, read on every background turn of this profile"
          }
          meta={meta(`bg-sets/${asset.id}#${open.role}`)}
          actions={
            <NamedDocumentActions
              readOnly={editor.readOnly}
              dirty={editor.dirty}
              saving={editor.saving}
              onBack={() => setOpenRole(null)}
              onSave={() => editor.save()}
            />
          }
        />

        <div className="space-y-6">
          <NamedConflictRecovery editor={editor} />
          <NamedRefusal editor={editor} />

          <Card>
            <CardBody className="space-y-4">
              <NamedProblems editor={editor} />
              {isTone ? (
                <BackgroundToneForm
                  documents={toneDocuments(editor.parts)}
                  parts={editor.parts}
                  readOnly={editor.readOnly}
                  onChange={editor.change}
                />
              ) : (
                <NamedDocumentEditor
                  documents={open.documents}
                  readOnly={editor.readOnly}
                  readOnlyReason={editor.readOnlyReason}
                  problems={editor.problems}
                  onChange={(documentId, text) =>
                    editor.change(
                      updateNamedDocument(editor.parts, documentId, text),
                    )
                  }
                />
              )}
              {saveControls}
            </CardBody>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        breadcrumbs={BREADCRUMBS}
        title={asset.name}
        description="Shared interview profile — the tone and prompts every background area of this variant is read under"
        meta={meta()}
        actions={
          <>
            {/* A COPY DESTINATION EXISTS NOW, which is what makes the seeded
                refusal an instruction rather than a dead end. Offered for every
                profile rather than only seeded ones: an authored profile sits
                at one variant coordinate and cannot be re-pointed, so copying
                it to a new variant is the ordinary way a second interview
                flavour is started. */}
            {/* GATED ON `library.write`, like every other creation control in
                this shell. A copy CREATES an asset, so a reader pressing it
                would reach a dialog whose only outcome is a refused POST —
                the dead control ADR 0001 forbids. The tooltip says which of
                the two reasons applies. */}
            <Tooltip
              content={
                write.reason ??
                (asset.capabilities.seeded
                  ? "Part of the base clinical library. The original stays as it is — make an editable copy to create your own version."
                  : "Start a new interview profile at another variant from this one's content.")
              }
            >
              <Button
                variant="outline"
                disabled={write.reason !== null}
                onClick={() => setCopying(true)}
              >
                Create editable copy
              </Button>
            </Tooltip>
            <Tooltip content={archiveRefusal ?? `${archiveLabel} this asset`}>
              <Button
                variant="outline"
                disabled={archiveRefusal !== null}
                onClick={() => setLifecycle(archiveMode)}
              >
                {archiveLabel}
              </Button>
            </Tooltip>
          </>
        }
      />

      <div className="space-y-6">
        <NamedConflictRecovery editor={editor} />
        <NamedRefusal editor={editor} />

        <Card>
          <CardHeader>
            <CardTitle>{ROWS_TITLE}</CardTitle>
          </CardHeader>
          <CardBody className="space-y-4">
            <p className="text-sm text-muted-foreground">{ROWS_DESCRIPTION}</p>
            <NamedProblems editor={editor} />
            {rows.length === 0 ? (
              <UnavailableState
                title="This version carries no documents"
                description="A shared interview profile stores a tone document and two prompts, and this version carries none. Its stored content is below, unchanged."
              />
            ) : (
              <NamedRowList
                rows={rows}
                // NO REORDER, NO REMOVE, NO ADD on this family, and each
                // absence is the service's rule rather than a simplification.
                // All three roles are REQUIRED of every version
                // (`placement.runtime_required_roles`), so a removal could
                // never be published and an add has nothing to add; and the
                // three files are opened by FIXED names, so their order in the
                // parts array reaches no runtime decision. Omitting the two
                // handlers is what says so — `readOnly` here would instead
                // claim the controls exist and are being withheld.
                readOnly={editor.readOnly}
                onOpen={setOpenRole}
              />
            )}
            {saveControls}
          </CardBody>
        </Card>

        <AssetAuditTrail assetId={asset.id} />
      </div>

      {lifecycle ? (
        <LifecycleDialog
          asset={asset}
          mode={lifecycle}
          onClose={() => setLifecycle(null)}
        />
      ) : null}

      {copying ? (
        <NewBackgroundProfileDialog
          copyFrom={asset}
          onClose={() => setCopying(false)}
        />
      ) : null}
    </>
  );
}
